/**
 * การจองฝากทศนิยม (user-classic) — เรียก Wallet API
 * POST/GET/PATCH .../wallet/tenant/:tenantId/decimal-deposits/...
 */
import { withApiBaseUrl } from '../lib/apiBaseUrl'
import { getOptionalTenantId } from '../utils/tenantUtils'
import { getPreferredUserIdentifier } from '../utils/userIdentity'

function getToken(): string | null {
  return localStorage.getItem('accessToken') || localStorage.getItem('authToken')
}

function getCurrentUser(): { id?: string; _id?: string; tenantId?: string } | null {
  try {
    const s = localStorage.getItem('user')
    return s ? JSON.parse(s) : null
  } catch {
    return null
  }
}

export interface CreateReservationResponse {
  success: boolean
  data?: {
    reservationId: string
    amount: number
    requestedAmount?: number
    decimalSuffix?: number
    expiresAt: string
    createdAt: string
  }
  error?: string
}

/** สร้างการจองฝากทศนิยม */
export async function createDecimalReservation(
  amount: number,
  options?: {
    expiresInMinutes?: number
    depositorPhone?: string
    depositorName?: string
    depositorBank?: string
    depositorAccount?: string
    webAccountName?: string
    webAccountNumber?: string
    webBankName?: string
  }
): Promise<CreateReservationResponse> {
  const user = getCurrentUser()
  const token = getToken()
  const tenantId = user?.tenantId || getOptionalTenantId(user || undefined)
  const userId = getPreferredUserIdentifier(user)
  if (!userId || !tenantId || !token) {
    return { success: false, error: 'ไม่พบข้อมูลผู้ใช้' }
  }
  try {
    const res = await fetch(
      withApiBaseUrl(`/api/v1/wallet/tenant/${encodeURIComponent(tenantId)}/decimal-deposits/reserve`),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID': tenantId,
        },
        credentials: 'include',
        body: JSON.stringify({
          amount: Number(amount),
          expiresInMinutes: options?.expiresInMinutes ?? 5,
          depositorPhone: options?.depositorPhone,
          depositorName: options?.depositorName,
          depositorBank: options?.depositorBank,
          depositorAccount: options?.depositorAccount,
          webAccountName: options?.webAccountName,
          webAccountNumber: options?.webAccountNumber,
          webBankName: options?.webBankName,
        }),
      }
    )
    const data = (await res.json()) as CreateReservationResponse
    if (!res.ok) return { success: false, error: data.error || 'สร้างการจองไม่สำเร็จ' }
    return data
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'เกิดข้อผิดพลาด' }
  }
}

/** ตรวจสอบสถานะการจอง (สำหรับ polling และ restore หลัง reload) */
export async function getReservationStatus(reservationId: string): Promise<{
  success: boolean
  data?: { status: string; expiresAt?: string; amount?: number; requestedAmount?: number; decimalSuffix?: number }
  error?: string
}> {
  const user = getCurrentUser()
  const token = getToken()
  const tenantId = user?.tenantId || getOptionalTenantId(user || undefined)
  if (!tenantId || !token) {
    return { success: false, error: 'ไม่พบข้อมูลผู้ใช้' }
  }
  try {
    const res = await fetch(
      withApiBaseUrl(`/api/v1/wallet/tenant/${encodeURIComponent(tenantId)}/decimal-deposits/${encodeURIComponent(reservationId)}/status`),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID': tenantId,
        },
        credentials: 'include',
      }
    )
    const data = (await res.json()) as {
      success: boolean
      data?: { status: string; expiresAt?: string; amount?: number; requestedAmount?: number; decimalSuffix?: number }
      error?: string
    }
    if (!res.ok) return { success: false, error: data.error || 'ตรวจสอบสถานะไม่สำเร็จ' }
    return data
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'เกิดข้อผิดพลาด' }
  }
}

/** ยกเลิกการจอง */
export async function cancelDecimalReservation(reservationId: string): Promise<{ success: boolean; error?: string }> {
  const user = getCurrentUser()
  const token = getToken()
  const tenantId = user?.tenantId || getOptionalTenantId(user || undefined)
  if (!tenantId || !token) {
    return { success: false, error: 'ไม่พบข้อมูลผู้ใช้' }
  }
  try {
    const res = await fetch(
      withApiBaseUrl(`/api/v1/wallet/tenant/${encodeURIComponent(tenantId)}/decimal-deposits/${encodeURIComponent(reservationId)}/cancel`),
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID': tenantId,
        },
        credentials: 'include',
      }
    )
    const data = (await res.json()) as { success: boolean; error?: string }
    if (!res.ok) return { success: false, error: data.error || 'ยกเลิกไม่สำเร็จ' }
    return data
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'เกิดข้อผิดพลาด' }
  }
}
